// ==================== 批量上架商品相关接口 ====================
// 接口列表：https://mock-test.cheweijia.com/project/10282/interface/api/cat_34189
import request from '@/utils/request'

/**
 * 检查是否需要展示推荐弹窗
 * @param {Object} data 参数
 * @param {string[]} data.popUpSceneList 弹框场景（batchOnlineGoods - 代表批量上架商品）
 * @returns {APIResponse<{popUp:number, popUpScene:string}>}
 * popUp-是否展示弹窗(0-不展示，1-展示)，popUpScene-弹窗场景
 */
export function checkRecommendPopup(data = {}) {
  return request({
    url: '/admin/v1/vehicle/popUp/check',
    method: 'POST',
    data,
  })
}

/**
 * 确认弹窗
 * @param {Object} data 参数
 * @param {string} data.popUpScene 弹框场景（batchOnlineGoods - 代表批量上架商品）
 * @param {string} data.action 操作类型（confirm-确认，ignore-忽略）
 * @returns {APIResponse<boolean>} code为1 - 成功其它失败
 */
export function recordRecommendPopupAction(data = {}) {
  const action = data.action || 'confirm'
  const url =
    action === 'confirm'
      ? '/admin/v1/vehicle/popUp/confirm'
      : '/admin/v1/vehicle/popUp/ignore'
  return request({
    url,
    method: 'POST',
    data,
  })
}

/**
 * 创建批量上架session（服务-客户端 以此建立一个短期session，保存批量上架的中间态数据）
 * @returns {APIResponse<{sessionId: string}>} sessionId-批量上架唯一标识
 */
export function getBatchSessionId() {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/createBatchOnlineSession',
    method: 'POST',
  })
}

/**
 * 销毁批量上架 Session
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @returns {Promise<void>}
 */
export function disposeBatchSession(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/deleteBatchOnlineSession',
    method: 'POST',
    data,
  })
}

/**
 * 创建服务端缓存
 *
 * 1. 此方法主要用在单租赁方案上架商品时，在选择车龄后调用
 * 2. 服务端需要根据前端传递的车龄创建缓存数据，供后续的批量上架使用
 *
 * 注：
 * - 批量上架商品时不需要单独调用此接口，因为批量上架商品有根据车龄获取车型列表的动作，服务会自动创建缓存
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @param {string} data.age - 车龄
 * @param {number} data.carModelId - 车型id（对应租赁方案详情的modelId字段）
 * @param {number} data.planId - 租赁方案id
 * @returns {APIResponse<boolean>} code为1 - 成功其它失败
 */
export function createBatchCache(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/singlePlanGenerateCacheData',
    method: 'POST',
    data,
  })
}

/**
 * @typedef {Object} BatchCarModel
 * @property {string} carBrandId - 品牌id
 * @property {string} carSeriesId - 车系id
 * @property {string} carModelId - 车型ID
 * @property {string} carModelName - 车型名称
 * @property {string} modelYear - 年款
 * @property {string} operationTypeName - 运营类型名称
 * @property {number} selectPlanAndCodeCount - 已选数量
 * @property {-1 | 1} groupCarModelInfoState - 车型信息状态 ：默认设置-1，单独设置1
 * @property {1 | 3} rentPeriodType - 租期类型  3-月租  1-日租 (选择租赁方案时使用)
 * @property {boolean} select - 是否已勾选 true-已勾选 false-未勾选
 * @property {boolean} canSelect - 是否可勾选 true-可勾选 false-不可勾选，不可勾选时前端禁用勾选操作
 */

/**
 * 获取车型列表（批量模式）
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @param {number} data.pageNum - 页码
 * @param {number} data.pageSize - 每页条数
 * @param {string} data.age - 车龄
 * @param {string} data.carSeriesId - 车系id
 * @param {string} data.carModelId - 车型id
 * @param {string} data.cityCode - cityCode
 * @returns {PagedResponse<BatchCarModel>} 车型列表，含分页信息
 */
export function getBatchCarModels(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/queryCarModelList',
    method: 'POST',
    data,
  })
}

/**
 * @typedef {Object} FeeItem
 * @property {string} name - 费用名称
 * @property {string} payDetailString - 费用金额, 如："100元/月"
 */

/**
 * @typedef {Object} RentPlanItem 租赁方案项
 * @property {string} id - 方案ID + 城市code 唯一标识
 * @property {number} planId - 租赁方案ID
 * @property {string} planName - 租赁方案名称
 * @property {string} cityCode - 城市code
 * @property {string} cityName - 城市名称
 * @property {string} rentPeriodInfo - 租期
 * @property {string} deposit - 押金
 * @property {string} dailyRentAmount - 日均租金
 * @property {FeeItem[]} otherFees - 其他费用
 * @property {string} presentDays - 赠送天数
 * @property {1 | 3} rentPeriodType - 租期类型  3-月租  1-日租
 */

/**
 * 获取租赁方案列表（批量模式）
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @param {string} data.carModelId - 车型ID
 * @param {1 | 3} data.rentPeriodType - 租期类型  3-月租  1-日租
 * @param {number} data.pageSize - pageSize
 * @param {number} data.pageNum - pageNum
 * @returns {PagedResponse<RentPlanItem>} 租赁方案列表
 */
export function getBatchPlanList(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/queryPlanAndCodeList',
    method: 'POST',
    data,
  })
}

/**
 * 保存已选择租赁方案列表
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @param {string} data.carModelId - 车型ID
 * @param {string[]} data.planAndCodeList - 已选择租赁方案id列表
 * @returns {APIResponse<boolean>} code为1 - 成功其它失败
 */
export function saveBatchPlans(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/updateCarModelGroupSelectPlanInfo',
    method: 'POST',
    data,
  })
}

/**
 * 设置全局车型配置（表显里程、有无车证， 车龄信息已经在选择车龄的时候告知服务端了），step1点击“下一步”时调用
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @param {number} data.mileage - 表显里程id
 * @param {0 | 1} data.carCert - 是否有车证 1-有 0-无
 * @returns {APIResponse<boolean>} code为1 - 成功其它失败
 */
export function setBatchCarConfig(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/updateMileageAndCarCertInfo',
    method: 'POST',
    data,
  })
}

/**
 * @typedef {Object} CarModelConfig 车型配置
 * @property {number} age - 车龄，默认值-1，代表默认设置
 * @property {number} mileage - 表显里程id, 默认值-1，代表默认设置
 * @property { -1 | 0 | 1} carCert - 是否有车证 1-有 0-无 -1-默认值默认设置
 */

/**
 * 获取车型配置（车龄、表显里程、有无车证）
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @param {string} data.carModelId - 车型ID
 * @returns {APIResponse<CarModelConfig>} 车型配置
 */
export function getSingleCarConfig(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/queryCarModelGroupAgeMileageInfo',
    method: 'POST',
    data,
  })
}

/**
 * 设置车型配置（车龄、表显里程、有无车证），单独设置车型配置弹框”保存“时调用
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @param {string} data.carModelId - 车型ID
 * @param {string} data.age - 车龄
 * @param {number} data.mileage - 表显里程id
 * @param {0 | 1} data.carCert - 是否有车证 1-有 0-无
 * @returns {APIResponse<boolean>} code为1 - 成功其它失败
 */
export function setSingleCarConfig(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/updateCarModelGroupAgeMileageInfo',
    method: 'POST',
    data,
  })
}

/**
 * 已选择租赁方案列表 和 每个城市可再选择租赁方案的数量（剩余配额）
 * @typedef {Object} ModelSelectedPlansAndCityQuota
 * @property {RentPlan[]} selectPlanAndCityCodeList - 已选择租赁方案列表
 * @property {Object.<string, number>} cityCode2CountMap - 每个城市可再选择租赁方案的数量，key-城市code，value-剩余配额数量
 */

/**
 * 获取车型 已选择租赁方案列表 和 每个城市可再选择租赁方案的数量（剩余配额）
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @param {string} data.carModelId - 车型ID
 * @returns {APIResponse<ModelSelectedPlansAndCityQuota>} 已选择租赁方案列表和城市配额信息
 */
export function getModelSelectedPlansAndCityQuota(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/queryCarModelGroupCountInfo',
    method: 'POST',
    data,
  })
}

/**
 * 勾选/反勾选车型
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @param {string} data.carModelId - 车型ID
 * @param {boolean} data.checked - 是否勾选
 * @returns {APIResponse<BatchCarModel>} 勾选/反勾选后的车型信息，返回BatchCarModel
 */
export function toggleCarModel(data = {}) {
  const url = data.checked
    ? '/admin/v1/vehicle/batchOnlineGoods/selectCarModelGroup'
    : '/admin/v1/vehicle/batchOnlineGoods/cancelCarModelGroup'

  return request({
    url,
    method: 'POST',
    data,
  })
}

/**
 * @typedef {Object} SelectedEntityAndStores 已选择的签约主体和门店
 * @property {number} enterpriseId - 签约主体ID
 * @property {number[]} storeIds - 门店ID列表
 * @property {number[]} cooperationPlatform - 合作平台列表
 */

/**
 * 获取已选择的签约主体和门店（用于 Step 2 组件初始化时恢复状态）
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @returns {APIResponse<SelectedEntityAndStores>} 已选择的签约主体和门店
 */
export function getSelectedStores(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/getEntityAndStore',
    method: 'POST',
    data,
  })
}

/**
 * 保存选择的主体和门店
 * 服务端返回code为80001时，代表有些租赁方案没有门店，让客户确认是否继续，如果选择继续，则再次调用本接口，给checkCity参数置为0，不校验
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @param {number} data.enterpriseId - 主体ID
 * @param {number[]} data.storeIds - 门店ID列表
 * @param {number[]} data.cooperationPlatform - 合作平台列表
 * @param {number} data.checkCity - 是否检查城市 0否1是 默认1是
 * @returns {<APIResponse<boolean>} code为1 - 成功, 80001 - 有些租赁方案没有门店，让客户确认是否继续，其它失败
 */
export function saveEntityAndStores(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/saveEntityAndStore',
    method: 'POST',
    data,
    bizCode: [80001],
  })
}

/**
 * @typedef {Object} BatchPreviewData 预览数据
 * @property {string} corporationName - 签约主体
 * @property {number} productCount - 商品数量
 * @property {number} storeCount - 门店数量
 */

/**
 * 获取预览数据
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @returns {APIResponse<BatchPreviewData>} 预览数据
 */
export function getBatchPreview(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/previewGoods/statistics',
    method: 'POST',
    data,
  })
}

/**
 * @typedef {Object} PreviewGoodsItem 预览商品项
 * @property {string} productName - 商品名称
 * @property {string} cityCode - cityCode
 * @property {string} cityName - cityName
 * @property {string} ageName - 车龄名称
 * @property {string} mileageName - 表显里程名称
 * @property {0|1} carCert - 是否有车证 1-有 0-无
 * @property {string} brandId - 品牌id
 * @property {string} brandName - 品牌名称
 * @property {string} seriesId - 车系id
 * @property {string} seriesName - 车系名称
 * @property {string} operationType - 运营类型
 * @property {string} carLevel - 车型等级
 * @property {string} carLevelName - 车型等级名称
 * @property {string} pictureUrl - 车型图片
 * @property {string} businessYears - 经营年份
 * @property {PreviewRentPlan[]} rentPlanList - 租赁方案列表
 * @property {string[]} topTags - 标签
 * @property {string[]} storeNameList - 门店列表
 * @property {string[]} cooperationPlatformList - 合作平台 名称列表
 */

/**
 * @typedef {Object} PreviewRentPlan 预览用租赁方案
 * @property {number} id - 租赁方案ID
 * @property {string} planName - 租赁方案名称
 * @property {number} rentPeriod - 租赁周期
 * @property {number} rentPeriodType - 租赁周期类型
 * @property {number} presentDays - 赠送天数
 * @property {string} rentAmount - 每期租金
 * @property {string} rentAmountUnit - 每期租金单位
 * @property {string} deposit - 押金
 * @property {number} depositPayMode - 押金支付方式
 * @property {number} rentCategory - 租赁类别
 * @property {number} state - 状态
 */

/**
 * 获取预览商品列表
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @param {number} data.pageNum - 页码
 * @param {number} data.pageSize - 每页条数
 * @returns {PagedResponse<PreviewGoodsItem>} 预览商品列表
 */
export function getBatchPreviewGoodsList(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/previewGoods/list',
    method: 'POST',
    data,
  })
}

/**
 * 批量商品上架
 * @param {Object} data 参数
 * @param {string} data.sessionId - 批量上架唯一标识
 * @returns {APIResponse<boolean>} code为1 - 成功其它失败
 */
export function submitBatchPublish(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/submit',
    method: 'POST',
    data,
  })
}

/**
 * 获取租赁方案详情
 * @param {Object} data 参数
 * @param {number} data.planId - 租赁方案id
 * @param {string} data.cityCode - 城市编码
 * @returns {APIResponse<RentPlanItem>} 租赁方案
 */
export function getSinglePlan(data = {}) {
  return request({
    url: '/admin/v1/vehicle/batchOnlineGoods/flushSinglePlanAndCode',
    method: 'POST',
    data,
  })
}
